import { useEffect, useRef, useState, useCallback } from "react";
import api, { downloadUrl, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Upload, FileText, Download, Trash2, GripVertical } from "lucide-react";

const BYTES_PER_KB = 1024;

export default function MaterialsTab({ courseId }) {
  const { user } = useAuth();
  const isInstructor = user?.role === "instructor";
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/courses/${courseId}/materials`);
      setItems(res.data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("file", file);
      await api.post(`/courses/${courseId}/materials`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTitle(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this material?")) return;
    try { await api.delete(`/materials/${id}`); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  // drag-to-reorder (instructor only)
  const dragIndex = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const onDrop = async (targetIndex) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    setDragOver(null);
    if (from === null || from === targetIndex) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(targetIndex, 0, moved);
    setItems(next); // optimistic
    try {
      await api.post(`/courses/${courseId}/materials/reorder`, { material_ids: next.map((m) => m.id) });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      load(); // revert to server order
    }
  };

  return (
    <div className="space-y-6">
      {isInstructor && (
        <form onSubmit={upload} className="nb-card-static p-5 space-y-3">
          <h3 className="font-display text-xl font-bold">Upload reading material</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <input data-testid="material-title-input" value={title} onChange={(e) => setTitle(e.target.value)} required className="nb-input" placeholder="Title (e.g. Chapter 3 notes)" />
            <input data-testid="material-file-input" ref={fileRef} type="file" required onChange={(e) => setFile(e.target.files?.[0] || null)} className="nb-input" />
          </div>
          <button data-testid="material-upload-btn" disabled={busy} className="nb-btn nb-btn-yellow flex items-center gap-2"><Upload className="w-4 h-4" /> {busy ? "Uploading..." : "Upload material"}</button>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </form>
      )}

      <div className="nb-card-static p-5">
        <h3 className="font-display text-xl font-bold mb-1">Reading materials</h3>
        {isInstructor && items.length > 1 && (
          <p className="text-xs text-[var(--muted-text)] mb-3">Drag the handle to arrange materials — students see this order.</p>
        )}
        {items.length === 0 && <p className="text-sm text-[var(--muted-text)]">No materials posted yet.</p>}
        <ul className="divide-y-2 divide-[var(--gold)]/30">
          {items.map((m, idx) => (
            <li
              key={m.id}
              data-testid={`material-${m.id}`}
              draggable={isInstructor}
              onDragStart={() => { dragIndex.current = idx; }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(idx); }}
              onDragLeave={() => setDragOver((d) => (d === idx ? null : d))}
              onDrop={(e) => { e.preventDefault(); onDrop(idx); }}
              onDragEnd={() => { dragIndex.current = null; setDragOver(null); }}
              className={`flex items-center justify-between py-3 gap-3 ${dragOver === idx ? "border-t-4 border-t-[var(--gold)]" : ""} ${isInstructor ? "cursor-default" : ""}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {isInstructor && (
                  <span data-testid={`drag-material-${m.id}`} className="cursor-grab active:cursor-grabbing text-[var(--muted-text)] hover:text-[var(--gold)] shrink-0" title="Drag to reorder">
                    <GripVertical className="w-5 h-5" />
                  </span>
                )}
                <div className="w-10 h-10 rounded-lg border-2 border-[var(--gold)] bg-[var(--blue)] flex items-center justify-center"><FileText className="w-5 h-5" /></div>
                <div className="min-w-0">
                  <p className="font-semibold truncate">{m.title}</p>
                  <p className="text-xs text-[var(--muted-text)] truncate">{m.filename} · {(m.size / BYTES_PER_KB).toFixed(1)} KB</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a data-testid={`download-material-${m.id}`} href={downloadUrl(`/materials/${m.id}/download`)} target="_blank" rel="noreferrer" className="nb-btn nb-btn-white text-sm flex items-center gap-1.5"><Download className="w-4 h-4" /> Download</a>
                {isInstructor && (
                  <button data-testid={`delete-material-${m.id}`} onClick={() => remove(m.id)} className="nb-btn nb-btn-white text-sm" title="Delete"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
